import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NiModalComponent } from '../lib/form/ni-modal/ni-modal.component';
import { NiButtonComponent } from '../lib/form/ni-button/ni-button.component';
import type { LogoutConfirmationVariant } from '../../core/services/staff-logout.service';

@Component({
  selector: 'app-logout-confirmation-modal',
  standalone: true,
  imports: [CommonModule, NiModalComponent, NiButtonComponent],
  template: `
    <NiFormModal
      [isOpen]="isOpen"
      [showCancelIcon]="false"
      [hasBackdrop]="true"
      [preventBackdropClose]="true"
      [className]="'max-w-[600px] w-full'"
      (onClose)="onCancel()">
      <div class="flex flex-col items-center gap-[1.5rem] w-full p-[40px] font-archivo-regular">
        <h2 class="text-[24px] font-regular text-[#111215] leading-[29px] text-center font-archivo font-bold">
          {{ modalTitle }}
        </h2>

        <p class="text-[#111215] leading-[32px] text-[18px] text-center">
          {{ modalMessage }}
        </p>

        <div class="flex gap-[24px] mt-[0.5rem] w-full items-center justify-center">
          <NiFormButton
            *ngIf="variant === 'general'"
            label="Cancel"
            variant="outlined"
            color="secondary"
            [fontWeight]="700"
            [fontSize]="18"
            paddingSize="px24_py_14"
            styleClass="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 min-w-[214px]"
            (onClick)="onCancel()"
          />
          <NiFormButton
            [label]="primaryButtonLabel"
            variant="outlined"
            color="secondary"
            [fontWeight]="700"
            [fontSize]="18"
            paddingSize="px24_py_14"
            [styleClass]="variant === 'program-view' || variant === 'general'
              ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 min-w-[214px] w-full max-w-[214px]'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 min-w-[214px]'"
            (onClick)="onLogout()"
          />
          <NiFormButton
            *ngIf="variant === 'program-edit'"
            label="Save & Exit"
            variant="contained"
            color="primary"
            [fontWeight]="700"
            [fontSize]="18"
            paddingSize="px24_py_14"
            styleClass="min-w-[214px]"
            (onClick)="onSaveAndExit()"
          />
        </div>
      </div>
    </NiFormModal>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class LogoutConfirmationModalComponent {
  @Input() isOpen = false;
  @Input() variant: LogoutConfirmationVariant = 'general';
  @Output() onLogoutClick = new EventEmitter<void>();
  @Output() onSaveAndExitClick = new EventEmitter<void>();
  @Output() onCancelClick = new EventEmitter<void>();

  get modalTitle(): string {
    switch (this.variant) {
      case 'general':
        return 'Log out?';
      case 'program-view':
        return 'Leave this application?';
      default:
        return 'Leave without saving?';
    }
  }

  get modalMessage(): string {
    switch (this.variant) {
      case 'general':
        return 'Are you sure you want to log out?';
      case 'program-view':
        return "You're viewing this application. Log out to exit.";
      default:
        return "You're in the middle of filling out this application. Save your progress to continue later, or log out without saving.";
    }
  }

  get primaryButtonLabel(): string {
    return this.variant === 'program-view' ? 'Exit' : 'Log Out';
  }

  onLogout(): void {
    this.onLogoutClick.emit();
  }

  onSaveAndExit(): void {
    this.onSaveAndExitClick.emit();
  }

  onCancel(): void {
    this.onCancelClick.emit();
  }
}
